import srp from 'secure-remote-password/client';
import { computed, ref, toRaw } from 'vue';
import {
  applySpec, assoc, path, prop,
} from 'ramda';
import useCallEndPointWith from '@/services/backend/useCallEndPointWith';
import { dummyUserState, useGlobalState } from '@/store/user.store';
import { isNotNullAndNotEmpty } from '@/utilities';
import { isNoAuthMode } from '../../config';

export const createEphemeral = () => srp.generateEphemeral();
export const getSalt = prop('salt');
export const getServerEphemeral = prop('serverEphemeral');
export const getProof = prop('proof');
export const getUserState = prop('userState');

export const useCreateSecureRemotePassword = (username, password) => {
  const salt = ref('');
  const verifier = ref('');
  salt.value = srp.generateSalt();
  const privateKey = srp.derivePrivateKey(salt.value, username, password);
  verifier.value = srp.deriveVerifier(privateKey);

  return { salt, verifier };
};

export const createClientSession = (options) => {
  const username = prop('email', options);
  const serverSalt = prop('serverSalt', options);
  const serverEphemeral = prop('serverEphemeral', options);
  const password = prop('password', options);
  const clientSecret = prop('clientSecret', options);
  const privateKey = srp.derivePrivateKey(serverSalt, username, password);
  return srp.deriveSession(clientSecret, serverEphemeral, serverSalt, username, privateKey);
};

export const getServerProofRequestSpec = applySpec({
  clientEphemeral: path(['clientEphemeral', 'public']),
  email: prop('email'),
});

export function fetchAndUpdateUserState() {
  const { callEndpoint, endpointData, isLoading } = useCallEndPointWith('userState');
  const {
    currentEmployees, userState, currentCourses, resetUserState,
  } = useGlobalState();

  const fetch = async () => {
    await callEndpoint({
      method: 'GET',
    }).then((response) => {
      const userStateFromEndpoint = isNoAuthMode() ? dummyUserState : getUserState(response);
      if (isNotNullAndNotEmpty(userStateFromEndpoint)) {
        userState.value = assoc('isAuthenticated', true, userStateFromEndpoint);
        currentEmployees.value = userStateFromEndpoint.employees;
        currentCourses.set(userStateFromEndpoint.courses);
      } else {
        resetUserState();
      }
    });
  };

  const response = computed(() => endpointData.value);

  return { fetch, response, isLoading };
}

export function useUserStateFromEndpoint() {
  const {
    callEndpoint: callEndpointSalt,
    endpointData: endpointDataSalt,
    userFriendlyError: userFriendlyErrorSalt,
    isLoading: isLoadingSalt,
  } = useCallEndPointWith('getSaltAndServerEphemeral');

  const {
    callEndpoint: loginFetchDataFromEndpoint,
    endpointData: endpointDataLogin,
    userFriendlyError: userFriendlyErrorLogin,
    isLoading: isLoadingLogin,
  } = useCallEndPointWith('login');

  const isLoading = computed(() => isLoadingSalt.value || isLoadingLogin.value);

  const getUserStateFromEndpoint = async (email, password) => {
    if (isNoAuthMode()) {
      return dummyUserState;
    }

    const clientEphemeral = createEphemeral();

    const requestForSalt = getServerProofRequestSpec({ clientEphemeral, email });

    await callEndpointSalt({
      request: requestForSalt,
      method: 'POST',
    });

    if (userFriendlyErrorSalt.value) {
      return {};
    }

    const clientSession = createClientSession({
      email,
      password,
      serverSalt: getSalt(endpointDataSalt.value),
      serverEphemeral: getServerEphemeral(endpointDataSalt.value),
      clientSecret: clientEphemeral.secret,
    });

    await loginFetchDataFromEndpoint({
      request: { proof: clientSession.proof },
      method: 'POST',
    });

    if (userFriendlyErrorLogin.value) {
      return {};
    }

    const proofPayload = toRaw(endpointDataLogin.value);
    const serverSessionProof = getProof(proofPayload);

    srp.verifySession(clientEphemeral.public, clientSession, serverSessionProof);

    return getUserState(proofPayload);
  };

  return {
    getUserStateFromEndpoint,
    isLoading,
    userFriendlyErrorSalt,
    userFriendlyErrorLogin,
  };
}
